import React from 'react';
import _ from 'lodash';
import qs from 'qs';
import { useLocation } from 'react-router-dom';
import { useQueryGetUnmanagedProjects } from 'hooks/queries';
import { collectItemsToCheck, collectItemsToUncheck } from './checkbox-logic';
import * as context from './context';

export const useSelectedProjects = () => {
  const selectedItems = React.useContext(context.selectedItems)?.selectedItems || {};
  const unmanagedProjects = useQueryGetUnmanagedProjects()?.data?.allProjects || [];
  const projectsSelectedToBeImported = _.filter(unmanagedProjects, ({ id }) => _.get(selectedItems, id));
  const numberOfProjectsInTotal = unmanagedProjects?.length;
  const selectedAll = projectsSelectedToBeImported?.length === numberOfProjectsInTotal;
  const selectedSome = !selectedAll && (projectsSelectedToBeImported?.length > 0);

  return {
    numberOfProjectsInTotal, selectedAll, selectedSome, projectsSelectedToBeImported,
  };
};

export const useParsedQueryString = () => {
  const search = useLocation()?.search;
  return _.merge({}, qs.parse(search, { ignoreQueryPrefix: true }));
};

export const useUpdatedTo = (newValues) => {
  const newQueryString = qs.stringify(_.assign(useParsedQueryString(), newValues));
  const pathname = useLocation()?.pathname;
  return { pathname, search: newQueryString };
};

const markAsMatchFilter = (filter, parentMatches) => (item) => {
  const matchesFilter = parentMatches || _.includes(
    _.toLower(`${item?.id},${item?.name}${_.flatMap(_.concat(item?.deepProjects || [], item?.deepFolders || []), ({ name, id }) => `${name},${id}`).join(',')}`),
    _.toLower(_.trim(filter || '')),
  );
  _.set(item, 'matchesFilter', matchesFilter);
};

export const useFilteredData = (targetFolder) => {
  const query = useQueryGetUnmanagedProjects();
  const filter = React.useContext(context.filter)?.filter;
  const folder = (targetFolder === '/') ? query?.data : _.find(query?.data?.deepFolders, { id: targetFolder });
  const data = folder;

  _.map(data?.deepFolders, markAsMatchFilter(filter, _.get(data, 'matchesFilter', false)));
  _.map(data?.deepProjects, markAsMatchFilter(filter, _.get(data, 'matchesFilter', false)));

  return [query, data];
};

const checkIfeveryChildIsSelected = (children, selectedItems) => {
  const isEveryChildSelected = children?.length && _.every(children, (item) => _.get(selectedItems, item?.id));
  return isEveryChildSelected;
};
const checkIfSomeChildrenAreSelected = (children, selectedItems) => {
  const isSomeChildSelected = children?.length && _.some(children, (item) => _.get(selectedItems, item?.id));
  return isSomeChildSelected;
};

export const useIsItemSelected = (id) => {
  const fullTree = useQueryGetUnmanagedProjects()?.data;
  const selectedItems = React.useContext(context.selectedItems)?.selectedItems;
  const itemIsDirectlySelected = !!_.get(selectedItems, [id]);
  const folder = _.find(_.get(fullTree, ['allFolders']), { id }) || {};
  const children = folder?.deepProjects || [];
  const everyChildIsSelected = checkIfeveryChildIsSelected(children, selectedItems);
  const someChildrenSelected = checkIfSomeChildrenAreSelected(children, selectedItems);
  const selected = everyChildIsSelected || itemIsDirectlySelected;
  const indeterminate = someChildrenSelected && !selected;
  return { selected, indeterminate };
};

export const useToggleFolderSelection = () => {
  const setSelectedItems = React.useContext(context.selectedItems)?.setSelectedItems;
  const fullTree = useQueryGetUnmanagedProjects()?.data;
  return React.useCallback(({ target }) => {
    const changes = _.merge({ [target?.value]: !!target?.checked },
      target?.checked ? collectItemsToCheck(fullTree, target?.value) : collectItemsToUncheck(fullTree, target?.value));
    setSelectedItems((previous) => _.merge({}, previous, changes));
  }, [setSelectedItems, fullTree]);
};

export const useToggleProjectSelection = () => {
  const setSelectedItems = React.useContext(context.selectedItems)?.setSelectedItems;
  const fullTree = useQueryGetUnmanagedProjects()?.data;
  return React.useCallback(({ target }) => {
    const changes = _.merge({ [target?.value]: !!target?.checked },
      target?.checked ? {} : collectItemsToUncheck(fullTree, target?.value));
    setSelectedItems((previous) => _.merge({}, previous, changes));
  }, [setSelectedItems, fullTree]);
};

export const useSmartNestedDepth = () => {
  const depth = (React.useContext(context.depth)?.depth || 0) + 1;
  const paddingLeft = `calc(${depth} * var(--rax-mgcp-nested-padding-multipler))`;
  return { depth, paddingLeft };
};
